import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ArticleNumberRequestResponse,
  ArticleNumberRequestStatus,
  BulkImportResult,
  CreateArticleNumberRequestInput,
  ImportArticleNumbersInput,
  ItemResponse,
  ListResponse,
  UpdateArticleNumberRequestInput,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

const KEY = ['article-number-requests'] as const;

/** Artikel-Anträge laden, optional nach Status gefiltert. */
export function useArticleNumberRequests(filter: { status?: ArticleNumberRequestStatus } = {}) {
  const query = filter.status ? `?status=${filter.status}` : '';
  return useQuery({
    queryKey: [...KEY, filter.status ?? 'ALL'],
    queryFn: () =>
      apiRequest<ListResponse<ArticleNumberRequestResponse>>(`/v1/article-number-requests${query}`),
  });
}

export function useCreateArticleNumberRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateArticleNumberRequestInput) =>
      apiRequest<ItemResponse<ArticleNumberRequestResponse>>('/v1/article-number-requests', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Offenen Antrag bearbeiten (engineering.update): nur Artikel-Details; Nummer bleibt fest. */
export function useUpdateArticleNumberRequest(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateArticleNumberRequestInput) =>
      apiRequest<ItemResponse<ArticleNumberRequestResponse>>(
        `/v1/article-number-requests/${id}`,
        { method: 'PATCH', body: JSON.stringify(input) },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useApproveArticleNumberRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<ArticleNumberRequestResponse>>(
        `/v1/article-number-requests/${id}/approve`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      // Der freigegebene Antrag legt einen Artikel an — Artikelliste auffrischen.
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

/**
 * Bestands-Artikelnummern importieren (engineering.approve): leitet Zeichnungsnummern
 * ab und legt echte Artikel an. Frischt Anträge, Zeichnungsnummern und Artikelliste auf.
 */
export function useImportArticleNumbers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ImportArticleNumbersInput) =>
      apiRequest<BulkImportResult>('/v1/article-number-requests/import', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      queryClient.invalidateQueries({ queryKey: ['drawing-numbers'] });
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

export function useRejectArticleNumberRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiRequest<ItemResponse<ArticleNumberRequestResponse>>(
        `/v1/article-number-requests/${id}/reject`,
        { method: 'POST', body: JSON.stringify({ reason }) },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Antrag zur Nachbesserung zurückgeben (engineering.approve) — Pflicht-Kommentar. */
export function useReturnArticleNumberRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      apiRequest<ItemResponse<ArticleNumberRequestResponse>>(
        `/v1/article-number-requests/${id}/return`,
        { method: 'POST', body: JSON.stringify({ comment }) },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Eigenen Antrag zurückziehen (Self-Service). */
export function useWithdrawArticleNumberRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<ArticleNumberRequestResponse>>(
        `/v1/article-number-requests/${id}/withdraw`,
        { method: 'POST' },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
